import { Box, Checkbox, Grid, Typography } from '@atgfr8/atg-ui-tailwind';
import { Notification, SelectInput } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import { FeatureFlags } from '@constants/featureFlags';
import { createAppStore } from '@store/createAppStore';
import {
  fetchCapacityGroups,
  updateCustomerDetailsState,
} from '@store/customers/customerDetails';
import { customerDetailsStore } from '@store/customers/customerDetails/store';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { isAdmin, isReadonly } from '@utils/utils';
import { NewATGOnlyNote } from '@views/order/components';
import { DateTime } from 'luxon';
import { Show, onMount } from 'solid-js';

import { approvedByCreditList } from '../constants';
import CustomerInformation from './CustomerInformation';
import TransportationDetails from './components/TransportationDetails';
import {
  CustomerGeneralUploadDocuments,
  GeneralCreditApplications,
  GeneralCreditRequest,
  GeneralCustomerContact,
  GeneralOwnerShip,
} from './components';
import GeneralRequestHistory from './components/GeneralRequestHistory';

type CapacityGroupsProps = {
  value: number;
  label: string;
};

const General = () => {
  const [capacityGroups, setCapacityGroups] = createAppStore<
    CapacityGroupsProps[]
  >([], 'capacityGroups', true);
  const DoNotSolicit =
    '"Do Not Solicit" list is at management’s discretion. Customer must be at $2M in annual gross revenue with Armstrong and have centralized decision-making process. Final decisions are made by the credit team.';

  const handleRequestedCustomerDoNotSolicit = (value: boolean) => {
    updateCustomerDetailsState({ requestedCustomerDoNotSolicit: value });
  };

  const handleFlagEmptyLoadsForReview = (value: boolean) => {
    updateCustomerDetailsState({ flagEmptyLoads: value });
  };

  onMount(async () => {
    const res = await fetchCapacityGroups();

    if (res && res.isSuccess) {
      // NOTE: Capacity group dropdown uses placeholder option value 'null' for native select.
      setCapacityGroups([
        ...res.value.map((item) => ({
          label: item.name as string,
          value: item.id,
        })),
      ]);
    }
  });
  return (
    <Grid container spacing={4}>
      <Show when={customerDetailsStore.isLoading}>
        <AtgLoader />
      </Show>
      <Grid item size={{ md: 12, lg: 7.5 }}>
        <CustomerInformation />
        {isAdmin() && <GeneralCreditRequest />}
        <GeneralOwnerShip capacityGroups={capacityGroups} />
      </Grid>
      <Grid item size={{ md: 12, lg: 4.5 }} class="min-w-0">
        <GeneralCustomerContact />
        <CustomerGeneralUploadDocuments />
        <TransportationDetails />
        {isAdmin() && <GeneralCreditApplications />}
        <Show when={customerDetailsStore.customer.requests.length > 0}>
          <GeneralRequestHistory />
        </Show>
        <NewATGOnlyNote
          type="Customer"
          cardTitle="Comments"
          comments={customerDetailsStore.customer.comments}
        />
        <Notification text={DoNotSolicit} type="info" />
        <Box class="my-4">
          <Typography
            variant="body2"
            component="span"
            sxProps={{ fontSize: '18px', fontWeight: '500' }}
          >
            Request Customer to be Added to "Do Not Solicit" List
          </Typography>
          <div class="flex gap-2 items-center">
            <Checkbox
              size="xs"
              color="info"
              label="Do Not Solicit"
              checked={Boolean(
                customerDetailsStore.customer.requestedCustomerDoNotSolicit,
              )}
              onChange={(value) => handleRequestedCustomerDoNotSolicit(value)}
              disabled={isReadonly()}
              dataTestId="donot-solicit-customer"
            />
            {isAdmin() && (
              <div class="flex-1 min-w-0">
                <Box class="flex items-center gap-2 mt-[10px]">
                  <div class="flex-1 min-w-0">
                    <SelectInput
                      label='Approved by Credit for "Do Not Solicit" List'
                      menuItems={approvedByCreditList.map((o) => ({
                        label: o.label,
                        value: String(o.value),
                      }))}
                      onChange={(val) =>
                        updateCustomerDetailsState({
                          isApprovedCustomerDoNotSolicit:
                            val === 'null' ? null : val === 'true',
                        })
                      }
                      value={String(
                        customerDetailsStore.customer
                          .isApprovedCustomerDoNotSolicit,
                      )}
                      dataTestId={formatDataTestId(
                        'Approved by credit do not solicit',
                        'selectInput',
                      )}
                    />
                  </div>
                  {(customerDetailsStore.customer
                    .isApprovedCustomerDoNotSolicit ??
                    false) && (
                    <span>
                      {customerDetailsStore.customer
                        .approvedCustomerDoNotSolicitDate != null
                        ? DateTime.fromISO(
                            customerDetailsStore.customer
                              .approvedCustomerDoNotSolicitDate,
                          ).toFormat('MM/dd/yyyy')
                        : DateTime.now().toFormat('MM/dd/yyyy')}
                    </span>
                  )}
                </Box>

                {(customerDetailsStore.customer.requestedCustomerDoNotSolicit ??
                  false) &&
                  (customerDetailsStore.customer
                    .isApprovedCustomerDoNotSolicit ??
                    false) && (
                    <Box class="text-red-500 font-medium">
                      Customer must also have centralized decision-making
                      process to appear on the Do Not Solicit list.
                    </Box>
                  )}
              </div>
            )}
          </div>
        </Box>
        <Show
          when={isFeatureFlagEnabled(FeatureFlags.AllowEmptyInLoadboardViews)()}
        >
          <Box class="my-4">
            <Typography
              variant="body2"
              component="span"
              sxProps={{ fontSize: '18px', fontWeight: '500' }}
            >
              Automatically flag Empty Loads for review.
            </Typography>
            <div>
              These loads will remain on loadboard until flag is cleared.
            </div>
            <div>
              Note: setting will only apply to loads created after setting is
              updated.
            </div>
            <Grid item>
              <Checkbox
                size="xs"
                color="info"
                label="Flag Loads for Review"
                checked={Boolean(customerDetailsStore.customer.flagEmptyLoads)}
                onChange={(value) => handleFlagEmptyLoadsForReview(value)}
                disabled={
                  isReadonly() &&
                  !isFeatureFlagEnabled(
                    FeatureFlags.LoadboardEmptyLoadsCustomerSetting,
                  )()
                }
                dataTestId="flag-loads-for-review"
              />
            </Grid>
          </Box>
        </Show>
      </Grid>
    </Grid>
  );
};
export default General;
